import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Sparkles, Send, Loader2, MapPin, Clock, Users, CreditCard, Heart } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AITravelPlanner() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ destination: '', budget: '', duration: '', interests: '', travelers: '' });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState('');
  const [error, setError] = useState('');

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPlan('');
    try {
      const res = await axios.post(`${API}/ai-travel-plan`, form);
      setPlan(res.data.plan);
    } catch (err) {
      setError('Failed to generate plan. Please try again.');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ destination: '', budget: '', duration: '', interests: '', travelers: '' });
    setPlan('');
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <button data-testid="ai-planner-trigger"
          className="fixed bottom-20 right-6 z-50 flex items-center gap-2 bg-navy text-white rounded-full px-5 py-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all font-body text-sm font-medium border border-gold/30">
          <Sparkles className="w-4 h-4 text-gold" />
          AI Travel Planner
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white" data-testid="ai-planner-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-gold" /> AI Travel Planner
          </DialogTitle>
          <DialogDescription className="font-body text-sm text-gray-500">Generate a personalized travel itinerary with AI</DialogDescription>
        </DialogHeader>

        {!plan ? (
          <form onSubmit={handleGenerate} className="space-y-4 mt-4">
            <p className="font-body text-sm text-gray-500">Tell us your preferences and our AI will create a personalized travel itinerary just for you.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-body text-xs font-medium text-navy mb-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" />Destination</label>
                <input type="text" value={form.destination} onChange={e => setForm({...form, destination: e.target.value})}
                  data-testid="ai-destination" placeholder="e.g., Bali, Dubai, Europe..."
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy mb-1 flex items-center gap-1"><CreditCard className="w-3 h-3 text-gold" />Budget (INR)</label>
                <input type="text" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})}
                  data-testid="ai-budget" placeholder="e.g., 50,000 per person"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy mb-1 flex items-center gap-1"><Clock className="w-3 h-3 text-gold" />Duration</label>
                <input type="text" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})}
                  data-testid="ai-duration" placeholder="e.g., 5-7 days"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="font-body text-xs font-medium text-navy mb-1 flex items-center gap-1"><Users className="w-3 h-3 text-gold" />Travelers</label>
                <input type="text" value={form.travelers} onChange={e => setForm({...form, travelers: e.target.value})}
                  data-testid="ai-travelers" placeholder="e.g., 2 adults, 1 child"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold" />
              </div>
            </div>

            <div>
              <label className="font-body text-xs font-medium text-navy mb-1 flex items-center gap-1"><Heart className="w-3 h-3 text-gold" />Interests</label>
              <input type="text" value={form.interests} onChange={e => setForm({...form, interests: e.target.value})}
                data-testid="ai-interests" placeholder="e.g., beaches, culture, adventure, food, nightlife..."
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold" />
            </div>

            {error && <p className="font-body text-sm text-red-500">{error}</p>}

            <button type="submit" disabled={loading} data-testid="ai-generate-btn"
              className="w-full flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full py-3 font-body font-semibold transition-all disabled:opacity-50">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating your plan...</> : <><Sparkles className="w-4 h-4" /> Generate Travel Plan</>}
            </button>
          </form>
        ) : (
          <div className="mt-4">
            <div data-testid="ai-plan-result" className="p-5 rounded-2xl bg-navy-50 border border-gray-100 max-h-[60vh] overflow-y-auto">
              <div className="font-body text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{plan}</div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={resetForm} className="flex-1 bg-navy text-white rounded-full py-3 font-body font-medium hover:bg-navy-light transition-all">
                Create Another Plan
              </button>
              <a href={`https://wa.me/919179992213?text=${encodeURIComponent(`Hi! I used your AI Travel Planner and I'm interested in this plan:\n\n${plan.substring(0, 500)}...\n\nPlease help me book this!`)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white rounded-full py-3 font-body font-medium hover:bg-green-600 transition-all">
                Book via WhatsApp
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
